import m from 'mithril';
import * as d3 from 'd3';
import * as topojson from 'topojson';

// Cities configuration
const CITIES = [
    { name: "New York", lat: 40.71, lng: -74.00 },
    { name: "Los Angeles", lat: 34.05, lng: -118.24 },
    { name: "London", lat: 51.50, lng: -0.12 },
    { name: "Paris", lat: 48.85, lng: 2.35 },
    { name: "Hong Kong", lat: 22.31, lng: 114.17 },
    { name: "Tokyo", lat: 35.67, lng: 139.65 },
    { name: "Sydney", lat: -33.86, lng: 151.20 },
    { name: "Moscow", lat: 55.75, lng: 37.61 },
    { name: "Beijing", lat: 39.90, lng: 116.40 },
    { name: "Cairo", lat: 30.04, lng: 31.23 },
    { name: "Rio de Janeiro", lat: -22.90, lng: -43.17 },
    { name: "Cape Town", lat: -33.92, lng: 18.42 },
    { name: "Mumbai", lat: 19.07, lng: 72.87 },
    { name: "Singapore", lat: 1.35, lng: 103.81 },
    { name: "Dubai", lat: 25.20, lng: 55.27 },
    { name: "Mexico City", lat: 19.43, lng: -99.13 },
    { name: "Toronto", lat: 43.65, lng: -79.38 },
    { name: "Sao Paulo", lat: -23.55, lng: -46.63 },
    { name: "Santiago", lat: -33.44, lng: -70.66 },
    { name: "Buenos Aires", lat: -34.60, lng: -58.38 },
    { name: "Lagos", lat: 6.52, lng: 3.37 },
    { name: "Istanbul", lat: 41.00, lng: 28.97 },
    { name: "Bangkok", lat: 13.75, lng: 100.50 },
    { name: "Jakarta", lat: -6.20, lng: 106.84 },
    { name: "Seoul", lat: 37.56, lng: 126.97 },
    { name: "Shanghai", lat: 31.23, lng: 121.47 },
    { name: "Perth", lat: -31.95, lng: 115.86 },
    { name: "Reykjavik", lat: 64.14, lng: -21.94 },
    { name: "Oslo", lat: 59.91, lng: 10.75 },
    { name: "Madrid", lat: 40.41, lng: -3.70 },
    { name: "Berlin", lat: 52.52, lng: 13.40 },
    { name: "New Delhi", lat: 28.61, lng: 77.20 },
    { name: "Manila", lat: 14.59, lng: 120.98 },
    { name: "Bogota", lat: 4.71, lng: -74.07 },
    { name: "Lima", lat: -12.04, lng: -77.04 },
    { name: "Antarctica", lat: -75.00, lng: 123.00 }
];

// Global state for time data
let TIME_LABELS = [];
let MAX_TIME_INDEX = 0;

// Fetch weather data
const fetchWeatherData = async () => {
    const lats = CITIES.map(c => c.lat).join(',');
    const lngs = CITIES.map(c => c.lng).join(',');
    
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lats}&longitude=${lngs}&hourly=temperature_2m&past_days=1&forecast_days=1`;

    try {
        const response = await fetch(url);
        const data = await response.json();
        
        if (Array.isArray(data)) {
            TIME_LABELS = data[0].hourly.time.map(t => {
                const date = new Date(t);
                return {
                    full: t,
                    display: date.toLocaleTimeString('en-US', { hour: '2-digit', minute:'2-digit', timeZone: 'UTC' }) + " UTC",
                    dateDisplay: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                };
            });
            MAX_TIME_INDEX = TIME_LABELS.length - 1;

            data.forEach((locData, index) => {
                CITIES[index].history = locData.hourly.temperature_2m;
                CITIES[index].temp = CITIES[index].history[MAX_TIME_INDEX]; 
            });
        }
        return true;
    } catch (e) {
        console.error("Weather fetch failed", e);
        return false;
    }
};

export const TemperatureGlobe = {
    oninit: (vnode) => {
        vnode.state.worldData = null;
        vnode.state.rotation = [0, -20];
        vnode.state.isDragging = false;
        vnode.state.lastX = 0;
        vnode.state.hoverCity = null;
        vnode.state.loading = true;
        vnode.state.timeIndex = 0;
        vnode.state.time = 0;
        vnode.state.mousePos = null;
        vnode.state.hoverPos = null;

        // Load Map
        d3.json("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json")
            .then(world => {
                vnode.state.worldData = topojson.feature(world, world.objects.countries);
                m.redraw();
            });

        // Load Weather
        fetchWeatherData().then(() => {
            vnode.state.loading = false;
            vnode.state.timeIndex = MAX_TIME_INDEX;
            m.redraw();
        });
    },
    oncreate: (vnode) => {
        const container = vnode.dom.querySelector('.canvas-wrapper');
        const canvas = vnode.dom.querySelector('canvas');
        const context = canvas.getContext('2d');
        
        let width = 0, height = 0, radius = 0;
        
        const projection = d3.geoOrthographic().clipAngle(90);
        const path = d3.geoPath().projection(projection).context(context);
        
        const colorScale = d3.scaleLinear()
            .domain([-30, 0, 15, 30, 45])
            .range(["#6366f1", "#06b6d4", "#84cc16", "#f59e0b", "#ef4444"]);

        const handleResize = () => {
            width = container.clientWidth;
            height = container.clientHeight;
            const dpr = window.devicePixelRatio || 1;
            canvas.width = width * dpr;
            canvas.height = height * dpr;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            context.scale(dpr, dpr);
            radius = Math.min(width, height) / 2.5;
            projection.scale(radius).translate([width / 2, height / 2]);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        const render = () => {
            if (!width || !height) return;

            context.clearRect(0, 0, width, height);
            const cx = width / 2;
            const cy = height / 2;

            // Background
            context.beginPath();
            path({type: "Sphere"});
            context.fillStyle = "#111827";
            context.fill();
            context.strokeStyle = "#1f2937";
            context.lineWidth = 1;
            context.stroke();
            
            // Land
            if (vnode.state.worldData) {
                context.beginPath();
                path(vnode.state.worldData);
                context.fillStyle = "#1f2937";
                context.fill();
                context.strokeStyle = "#374151";
                context.lineWidth = 0.5;
                context.stroke();
            }

            // Cities
            const r = projection.rotate();
            const centerLng = -r[0];
            const centerLat = -r[1];

            let closestDist = Infinity;
            let hoveredCityData = null;
            let hoveredCoords = null;

            const mx = vnode.state.mousePos ? vnode.state.mousePos[0] : -1000;
            const my = vnode.state.mousePos ? vnode.state.mousePos[1] : -1000;

            vnode.state.time += 0.02;
            const pulse = Math.abs(Math.sin(vnode.state.time));

            const currentIndex = vnode.state.timeIndex;

            CITIES.forEach(city => {
                if (d3.geoDistance([city.lng, city.lat], [centerLng, centerLat]) > 1.57) return;

                const coords = projection([city.lng, city.lat]);
                if (!coords) return;

                const currentTemp = city.history ? city.history[currentIndex] : city.temp;

                if (currentTemp !== undefined) {
                    const color = colorScale(currentTemp);
                    
                    // Glow
                    context.beginPath();
                    context.fillStyle = color;
                    context.globalAlpha = 0.2 * pulse;
                    context.arc(coords[0], coords[1], 8 + (pulse * 4), 0, 2 * Math.PI);
                    context.fill();

                    // Core
                    context.beginPath();
                    context.globalAlpha = 1.0;
                    context.fillStyle = color;
                    context.arc(coords[0], coords[1], 3, 0, 2 * Math.PI);
                    context.fill();

                    // Ring
                    context.beginPath();
                    context.strokeStyle = color;
                    context.lineWidth = 1.5;
                    context.arc(coords[0], coords[1], 5, 0, 2 * Math.PI);
                    context.stroke();

                    // Tooltip Logic
                    const dx = mx - coords[0];
                    const dy = my - coords[1];
                    const dist = Math.sqrt(dx*dx + dy*dy);
                    if (dist < 15 && dist < closestDist) {
                        closestDist = dist;
                        hoveredCityData = { ...city, displayTemp: currentTemp };
                        hoveredCoords = coords;
                    }
                }
            });

            // Atmosphere
            const grad = context.createRadialGradient(cx, cy, radius, cx, cy, radius * 1.3);
            grad.addColorStop(0, "rgba(56, 189, 248, 0.05)");
            grad.addColorStop(1, "rgba(0,0,0,0)");
            context.fillStyle = grad;
            context.fillRect(0,0,width,height);

            // Hover Ring
            if (hoveredCityData) {
                context.beginPath();
                context.strokeStyle = "#fff";
                context.lineWidth = 2;
                context.arc(hoveredCoords[0], hoveredCoords[1], 10, 0, 2 * Math.PI);
                context.stroke();
                vnode.state.hoverCity = hoveredCityData;
                vnode.state.hoverPos = hoveredCoords;
            } else {
                vnode.state.hoverCity = null;
            }

            m.redraw();
        };

        const timer = d3.timer(() => {
            if (!vnode.state.isDragging) {
                const rot = vnode.state.rotation;
                projection.rotate([rot[0] + 0.05, rot[1]]);
                vnode.state.rotation = projection.rotate();
            } else {
                projection.rotate(vnode.state.rotation);
            }
            render();
        });

        d3.select(canvas)
            .call(d3.drag()
                .on("start", (e) => {
                    vnode.state.isDragging = true;
                    vnode.state.lastX = e.x;
                    vnode.state.lastY = e.y;
                })
                .on("drag", (e) => {
                    const dx = e.x - vnode.state.lastX;
                    const dy = e.y - vnode.state.lastY;
                    const r = projection.rotate();
                    vnode.state.rotation = [r[0] + dx * 0.3, Math.max(-85, Math.min(85, r[1] - dy * 0.3))];
                    vnode.state.lastX = e.x;
                    vnode.state.lastY = e.y;
                })
                .on("end", () => vnode.state.isDragging = false)
            )
            .on("mousemove", (e) => {
                const rect = canvas.getBoundingClientRect();
                vnode.state.mousePos = [e.clientX - rect.left, e.clientY - rect.top];
            })
            .on("mouseleave", () => vnode.state.mousePos = null);

        vnode.state.onremove = () => {
            timer.stop();
            window.removeEventListener('resize', handleResize);
        };
    },
    onremove: (vnode) => {
        if (vnode.state.onremove) vnode.state.onremove();
    },
    view: (vnode) => {
        const currentTimeLabel = TIME_LABELS[vnode.state.timeIndex] || { display: "--:--", dateDisplay: "--" };

        return m("div", { class: "w-full max-w-6xl mx-auto px-4" }, [
            m("div", { class: "text-center mb-4" }, [
                m("p", { class: "text-sm opacity-50 font-mono" }, "Data Source: Open-Meteo API (Last 24h)")
            ]),
            m("div", { class: "relative w-full h-[600px] flex flex-col rounded-lg overflow-hidden border border-white/10" }, [
                // Header
                m("div", { class: "absolute top-6 left-6 z-10 pointer-events-none" }, [
                    m("h1", { class: "text-2xl font-display font-bold tracking-tight" }, "Historical Weather"),
                    m("div", { class: "flex items-center gap-3 mt-2 text-sm text-gray-400 font-mono" }, [
                        vnode.state.loading 
                            ? m("div", { class: "loader" }) 
                            : m("div", { class: "w-2 h-2 rounded-full bg-blue-500" }),
                        m("span", vnode.state.loading ? "Loading History..." : "Live Data")
                    ])
                ]),

                // Canvas
                m("div", { class: "canvas-wrapper flex-1 relative cursor-move" }, [
                    m("canvas"),
                    
                    // Tooltip
                    vnode.state.hoverCity && vnode.state.hoverPos ? 
                    m("div", {
                        class: "absolute pointer-events-none bg-slate-900/90 backdrop-blur border border-slate-700 px-4 py-3 rounded-lg shadow-2xl transform -translate-x-1/2 -translate-y-full mt-[-15px] min-w-[140px]",
                        style: `left: ${vnode.state.hoverPos[0]}px; top: ${vnode.state.hoverPos[1]}px;`
                    }, [
                        m("div", { class: "text-xs text-gray-400 uppercase tracking-wider mb-1" }, vnode.state.hoverCity.name),
                        m("div", { class: "text-2xl font-mono font-bold text-white" }, [
                            vnode.state.hoverCity.displayTemp,
                            m("span", { class: "text-lg text-gray-500 ml-1" }, "°C")
                        ])
                    ]) : null
                ]),

                // SLIDER CONTROLS (Bottom Center)
                m("div", { class: "absolute bottom-8 left-1/2 transform -translate-x-1/2 w-full max-w-2xl px-6 z-20" }, [
                    m("div", { class: "bg-slate-900/80 backdrop-blur border border-slate-700 rounded-2xl p-4 shadow-2xl" }, [
                        // Time Display
                        m("div", { class: "flex justify-between items-end mb-2" }, [
                            m("div", [
                                m("div", { class: "text-xs text-gray-400 uppercase tracking-wider" }, "Selected Time (UTC)"),
                                m("div", { class: "text-xl font-mono font-bold text-blue-400" }, currentTimeLabel.display)
                            ]),
                            m("div", { class: "text-right" }, [
                                m("div", { class: "text-xs text-gray-400 uppercase tracking-wider" }, "Date"),
                                m("div", { class: "text-lg font-mono text-white" }, currentTimeLabel.dateDisplay)
                            ])
                        ]),

                        // Range Input
                        m("input", {
                            type: "range",
                            min: 0,
                            max: MAX_TIME_INDEX,
                            value: vnode.state.timeIndex,
                            oninput: (e) => {
                                vnode.state.timeIndex = parseInt(e.target.value);
                            },
                            class: "weather-slider"
                        }),

                        // Labels (Start/End)
                        m("div", { class: "flex justify-between mt-2 text-xs text-gray-500 font-mono" }, [
                            m("span", "-24 Hours"),
                            m("span", "Now")
                        ])
                    ])
                ]),

                // Legend (Top Right)
                m("div", { class: "absolute right-6 top-6 bg-slate-900/80 p-4 rounded-xl backdrop-blur border border-slate-800 pointer-events-none" }, [
                    m("div", { class: "text-xs text-gray-400 mb-2 uppercase tracking-wider text-center" }, "Temp (°C)"),
                    m("div", { class: "flex items-center gap-3" }, [
                        m("span", { class: "text-xs font-mono text-blue-400" }, "-30"),
                        m("div", { 
                            class: "w-32 h-2 rounded-full",
                            style: "background: linear-gradient(to right, #6366f1, #06b6d4, #84cc16, #f59e0b, #ef4444);" 
                        }),
                        m("span", { class: "text-xs font-mono text-red-400" }, "45"),
                    ])
                ])
            ])
        ]);
    }
};
